import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Swords, Coins, Loader2, Maximize2 } from "lucide-react";
import DraftBoard from "../components/DraftBoard";
import PitchView from "../components/PitchView";
import Lobby from "../components/Lobby";
import LiveSimulation from "../components/LiveSimulation";
import SigningPopup from "../components/SigningPopup";
import WagerSignupModal from "../components/WagerSignupModal";
import AuthForm from "../components/AuthForm";
import { Dialog, DialogContent, DialogTitle } from "../components/ui/dialog";
import { auth, api, getToken, getStoredUser, summaryFrom, shareLink } from "../lib/api";
import { GuestLock } from "../components/GuestUnlock";
import { useToast } from "../hooks/use-toast";
import { Toaster } from "../components/ui/toaster";

const TopBar = ({ navigate, coins }) => (
  <div className="sticky top-0 z-40 bg-[#0F172A]/85 backdrop-blur-md border-b border-white/5">
    <div className="max-w-5xl mx-auto px-4 h-14 flex items-center justify-between">
      <button
        data-testid="matchplay-back-btn"
        onClick={() => navigate("/multiplayer")}
        className="pill-nav rounded-full px-3.5 py-1.5 flex items-center gap-2 text-sm font-medium text-slate-200"
      >
        <ArrowLeft className="w-4 h-4" /> Lobby
      </button>
      <div className="font-display font-extrabold text-base sm:text-lg flex items-center gap-1.5 tracking-tight">
        <Swords className="w-4 h-4 text-[#10B981]" />
        <span className="text-white">FOOTIE</span>
        <span className="text-[#38BDF8]">CLASH</span>
      </div>
      <div className="flex items-center gap-1.5 rounded-full bg-[#10B981]/12 border border-[#10B981]/30 px-3 py-1.5">
        <Coins className="w-4 h-4 text-[#10B981]" />
        <span className="font-display font-bold text-sm text-[#10B981]">{coins == null ? "—" : `£${coins.toLocaleString()}`}</span>
        <GuestLock />
      </div>
    </div>
  </div>
);

const OpponentPreview = ({ part, onExpand }) => {
  if (!part) return null;
  const filled = part.board?.filled ?? 0;
  return (
    <button
      data-testid="opponent-preview-btn"
      onClick={onExpand}
      className="fixed bottom-4 right-4 z-40 w-28 sm:w-32 rounded-xl border border-white/15 bg-[#1E293B]/95 backdrop-blur p-2 shadow-xl hover:border-[#38BDF8]/60 transition-colors text-left"
    >
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] font-semibold truncate text-white max-w-[64px]">{part.username}</span>
        {part.locked ? (
          <span className="text-[8px] font-bold text-emerald-400">LOCKED</span>
        ) : (
          <span className="text-[8px] text-muted-38">{filled}/11</span>
        )}
      </div>
      <div className="pointer-events-none">
        <PitchView board={part.board} compact />
      </div>
      <div className="flex items-center justify-center gap-1 text-[9px] text-muted-38 mt-1">
        <Maximize2 className="w-2.5 h-2.5" /> Tap to expand
      </div>
    </button>
  );
};

const MatchPlay = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { id } = useParams();

  const [user, setUser] = useState(getStoredUser());
  const [coins, setCoins] = useState(getStoredUser()?.coins ?? null);
  const [phase, setPhase] = useState("loading"); // loading | gate | ready
  const [match, setMatch] = useState(null);
  const [iLocked, setILocked] = useState(false);
  const [myBoard, setMyBoard] = useState(null);
  const [joining, setJoining] = useState(false);
  const [readying, setReadying] = useState(false);
  const [rematching, setRematching] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [signing, setSigning] = useState(null);
  const signingQueue = useRef([]);
  const seenPlayers = useRef(null);

  const idRef = useRef(id);
  const statusRef = useRef(null);
  const lockedRef = useRef(false);
  const coinsRefreshed = useRef(false);
  const autoJoined = useRef(false);
  const rematchRef = useRef(null);

  const inviteLink = shareLink("w", id);

  // Detect fresh 90+ signings on either board and queue a celebratory popup.
  useEffect(() => {
    if (!match || match.status !== "drafting") return;
    const sides = [match.creator, match.opponent].filter(Boolean);
    const current = new Map();
    sides.forEach((side) => {
      (side.board?.xi || []).forEach((slot) => {
        if (slot.player && typeof slot.player.rating === "number") {
          current.set(`${side.username}:${slot.player.name}`, { side, player: slot.player });
        }
      });
    });
    if (seenPlayers.current === null) {
      // Seed on first drafting snapshot so existing picks don't all fire at once.
      seenPlayers.current = new Set(current.keys());
      return;
    }
    current.forEach((val, key) => {
      if (!seenPlayers.current.has(key)) {
        seenPlayers.current.add(key);
        if (val.player.rating >= 90) {
          signingQueue.current.push({ name: val.side.username, player: val.player.name, rating: val.player.rating });
        }
      }
    });
    if (!signing && signingQueue.current.length) {
      setSigning(signingQueue.current.shift());
    }
  }, [match, signing]);

  const nextSigning = useCallback(() => {
    setSigning(signingQueue.current.length ? signingQueue.current.shift() : null);
  }, []);

  useEffect(() => {
    idRef.current = id;
  }, [id]);

  useEffect(() => {
    auth
      .ensure()
      .then((u) => {
        if (!u) return setPhase("gate");
        setUser(u);
        setCoins(u.coins);
        setPhase("ready");
      })
      .catch(() => setPhase("gate"));
  }, []);

  // Poll the match state throughout the live flow.
  useEffect(() => {
    if (phase !== "ready") return;
    let active = true;
    const tick = async () => {
      try {
        const m = await api.getMatch(id);
        if (active) {
          setMatch(m);
          statusRef.current = m.status;
          rematchRef.current = m.rematch_id || null;
          if (
            m.status === "lobby" && m.open && !m.opponent &&
            user && m.creator.email !== user.email && !autoJoined.current
          ) {
            autoJoined.current = true;
            doJoin();
          }
        }
      } catch (e) {
        /* ignore transient errors */
      }
    };
    tick();
    const t = setInterval(() => {
      if (statusRef.current === "cancelled" || rematchRef.current) return;
      tick();
    }, 1500);
    return () => {
      active = false;
      clearInterval(t);
    };
  }, [phase, id]);

  // Refresh wallet once the match settles.
  useEffect(() => {
    if (match?.status === "complete" && !coinsRefreshed.current) {
      coinsRefreshed.current = true;
      auth
        .me()
        .then((u) => {
          setUser(u);
          setCoins(u.coins);
        })
        .catch(() => {});
    }
  }, [match?.status]);

  const onAuthed = (u) => {
    setUser(u);
    setCoins(u.coins);
    setPhase("ready");
  };

  const handleBoardChange = useCallback((b) => {
    const compact = {
      formation: b.formation,
      filled: b.filled,
      xi: b.xi.map((s) => ({
        id: s.id,
        x: s.x,
        y: s.y,
        label: s.label,
        cat: s.cat,
        player: s.player
          ? { short: s.player.short, color: s.player.color, rating: s.player.rating, name: s.player.name }
          : null,
      })),
    };
    setMyBoard(compact);
    if (idRef.current && statusRef.current === "drafting" && !lockedRef.current) {
      api.pushBoard(idRef.current, compact).catch(() => {});
    }
  }, []);

  const handleLock = async (result) => {
    setILocked(true);
    lockedRef.current = true;
    try {
      const res = await api.lockMatch(idRef.current, summaryFrom(result));
      setMatch(res.match);
      statusRef.current = res.match.status;
      if (res.coins != null) setCoins(res.coins);
    } catch (e) {
      toast({ title: "Couldn't lock in", description: e?.response?.data?.detail || "Try again" });
    }
  };

  const doJoin = async () => {
    setJoining(true);
    try {
      const res = await api.joinMatch(id);
      setMatch(res.match);
      statusRef.current = res.match.status;
      const u = await auth.me();
      setUser(u);
      setCoins(u.coins);
    } catch (e) {
      toast({ title: "Couldn't join", description: e?.response?.data?.detail || "Try again" });
    } finally {
      setJoining(false);
    }
  };

  const doReady = async () => {
    setReadying(true);
    try {
      const res = await api.readyMatch(id);
      setMatch(res.match);
      statusRef.current = res.match.status;
    } catch (e) {
      toast({ title: "Couldn't ready up", description: e?.response?.data?.detail || "Try again" });
    } finally {
      setReadying(false);
    }
  };

  const doCancel = async () => {
    try {
      const res = await api.cancelMatch(id);
      if (res.coins != null) setCoins(res.coins);
    } catch (e) {
      /* ignore */
    }
    navigate("/multiplayer");
  };

  const goToMatch = (newId) => {
    setILocked(false);
      lockedRef.current = false;
      setMyBoard(null);
      coinsRefreshed.current = false;
      statusRef.current = null;
      setMatch(null);
      autoJoined.current = false;
      rematchRef.current = null;
      navigate(`/multiplayer/match/${newId}`);
  };

  const doRematch = async () => {
    setRematching(true);
    try {
      const res = await api.rematchMatch(id);
      goToMatch(res.rematch_id);
    } catch (e) {
      toast({ title: "Couldn't start rematch", description: e?.response?.data?.detail || "Try again" });
    } finally {
      setRematching(false);
    }
  };

  // ---------- render ----------
  if (phase === "loading") {
    return (
      <div className="bg-app min-h-screen flex items-center justify-center text-slate-400">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  if (phase === "gate") {
    return (
      <div className="bg-app min-h-screen text-white relative overflow-hidden">
        <div className="absolute inset-0 hero-glow pointer-events-none" />
        <TopBar navigate={navigate} coins={coins} />
        <div className="relative z-10 max-w-md mx-auto px-5 py-14">
          <AuthForm onAuthed={onAuthed} title="You've been challenged!" subtitle="Sign up free (£30 virtual to start) and you'll go straight into the draft." />
        </div>
        <Toaster />
      </div>
    );
  }

  const content = () => {
    if (!match || !user) {
      return (
        <div className="min-h-[60vh] flex items-center justify-center text-slate-400">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      );
    }

    if (match.status === "cancelled") {
      return (
        <div className="relative z-10 max-w-md mx-auto px-5 py-14">
          <div className="card-38 p-6 text-center hover:translate-y-0" data-testid="match-cancelled">
            <p className="text-slate-300">This challenge was cancelled and any stakes were refunded.</p>
            <button onClick={() => navigate("/multiplayer")} className="btn-green rounded-xl px-6 py-3 font-semibold mt-4">
              Back to lobby
            </button>
          </div>
        </div>
      );
    }

    if (match.status === "complete") {
      return (
        <LiveSimulation
          match={match}
          user={user}
          onPlayAgain={() => navigate("/multiplayer")}
          onRematch={doRematch}
          onAcceptRematch={() => goToMatch(match.rematch_id)}
          rematching={rematching}
          friendly={match.friendly || match.stake === 0}
        />
      );
    }

    if (match.status === "lobby") {
      return (
        <Lobby
          match={match}
          user={user}
          onJoin={doJoin}
          onReady={doReady}
          onCancel={doCancel}
          joining={joining}
          readying={readying}
          inviteLink={inviteLink}
        />
      );
    }

    // drafting
    const amCreator = match.creator.email === user.email;
    const myPart = amCreator ? match.creator : match.opponent?.email === user.email ? match.opponent : null;
    const oppPart = amCreator ? match.opponent : match.creator;
    const myLocked = iLocked || !!myPart?.locked;

    return (
      <>
        <SigningPopup signing={signing} onDone={nextSigning} />
        <div className="max-w-5xl mx-auto px-4 pt-4">
          <div className="card-38 p-4 flex items-center justify-between hover:translate-y-0">
            <div className="flex items-center gap-3">
              <Swords className="w-5 h-5 text-[#10B981]" />
              <div>
                <div className="font-semibold text-white text-sm">Live draft vs {oppPart?.username}</div>
                <div className="text-muted-38 text-xs">
                  Stake {match.stake} · Winner takes {match.pot}
                </div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-muted-38 text-[10px]">POT</div>
              <div className="font-display font-bold text-[#10B981] text-lg">{match.pot}</div>
            </div>
          </div>
        </div>

        {!myLocked ? (
          <DraftBoard onBoardChange={handleBoardChange} onSimulate={handleLock} ctaLabel="Lock In XI" />
        ) : (
          <div className="max-w-md mx-auto px-4 py-8" data-testid="locked-waiting">
            <div className="card-38 p-6 text-center hover:translate-y-0">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-emerald-500/15 border border-emerald-500/30 mb-3">
                <Loader2 className="w-7 h-7 text-emerald-400 animate-spin" />
              </div>
              <h1 className="font-display font-bold text-2xl">XI locked in!</h1>
              <p className="text-slate-400 text-sm mt-1">
                Waiting for {oppPart?.username} to finish drafting — the season kicks off the moment they lock in.
              </p>
              <div className="mt-5">
                <PitchView board={myBoard || myPart?.board} />
              </div>
            </div>
          </div>
        )}

        <OpponentPreview part={oppPart} onExpand={() => setExpanded(true)} />

        <Dialog open={expanded} onOpenChange={setExpanded}>
          <DialogContent aria-describedby={undefined} className="w-[calc(100%-2rem)] bg-[#1E293B] border border-white/10 text-white max-w-sm p-4">
            <DialogTitle className="font-display font-bold text-lg mb-1">{oppPart?.username}'s XI</DialogTitle>
            <div className="text-muted-38 text-xs mb-3">
              {oppPart?.locked ? "Locked in" : `${oppPart?.board?.filled ?? 0}/11 drafted`}
            </div>
            <PitchView board={oppPart?.board} />
          </DialogContent>
        </Dialog>
      </>
    );
  };

  return (
    <div className="bg-app min-h-screen text-white relative overflow-hidden">
      <div className="absolute inset-0 hero-glow pointer-events-none" />
      <TopBar navigate={navigate} coins={coins} />
      {content()}
      <Toaster />
    </div>
  );
};

export default MatchPlay;
